import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, Stack, Group, Text, Button, Badge, Progress, Skeleton } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { notifyUndo } from '../../lib/toast';
import { channelWa, contacts, templates, inbox } from '@repletoryreach/sdk-api';
import { usePersistedState } from '../../lib/usePersistedState';
import { useT } from '../../i18n/I18nProvider';

interface Task {
  key: string;
  label: string;
  hint: string;
  doneHint: string;
  cta: string;
  to: string;
  done: boolean;
}

/**
 * First-run setup checklist on the dashboard. Each task reflects live state and
 * links to the page that completes it. Dismissible: the choice persists.
 *
 * Rebuilt per `_UI_improvements/tenant/_app-shell.md` §4 item 9 and §6:
 * - every string is catalogued (all eleven were hardcoded English);
 * - a `Progress` bar and numbered steps replace the bare "2/4" counter;
 * - `Skeleton` rows while the four queries are pending, instead of flashing 0/4;
 * - a failed query is no longer silently rendered as "not done": an error
 *   alert says the state is unknown, because "you haven't attached a number"
 *   is a damaging thing to tell someone who has;
 * - the contact/conversation queries `select` only the total, so the cached
 *   page objects are not retained.
 */
export function SetupChecklist() {
  const t = useT();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = usePersistedState('tenant.setupChecklist.dismissed', false);

  const numbers = useQuery({
    queryKey: ['numbers'],
    queryFn: () => channelWa.listPhoneNumbers(),
    select: (p) => (p.data?.length ?? 0) > 0
  });
  const contactsQ = useQuery({
    queryKey: ['contacts', 'count'],
    queryFn: () => contacts.listContacts({ limit: 1 }),
    select: (p) => (p.meta?.total ?? 0) > 0
  });
  const templatesQ = useQuery({
    queryKey: ['templates'],
    queryFn: () => templates.list(),
    select: (p) => (p.data?.length ?? 0) > 0
  });
  const convs = useQuery({
    queryKey: ['conversations', 'count'],
    queryFn: () => inbox.listConversations({ limit: 1 }),
    select: (p) => (p.meta?.total ?? 0) > 0
  });

  /*
   * Dismissing used to be permanent and silent: a user who tapped it at 1/4
   * lost their onboarding path with no way back (§4 item 12). It is now
   * undoable from the toast, and from Settings → Appearance.
   */
  const dismiss = () => {
    setDismissed(true);
    notifyUndo({
      message: t('setup.dismissed'),
      undoLabel: t('action.undo'),
      onUndo: () => setDismissed(false)
    });
  };

  const queries = [numbers, contactsQ, templatesQ, convs];
  const pending = queries.some((q) => q.isPending);
  const failed = queries.some((q) => q.isError);

  const tasks: Task[] = [
    {
      key: 'number',
      label: t('setup.task.number.label'),
      hint: t('setup.task.number.hint'),
      doneHint: t('setup.task.number.done'),
      cta: t('nav.numbers'),
      to: '/numbers',
      done: numbers.data === true
    },
    {
      key: 'contacts',
      label: t('setup.task.contacts.label'),
      hint: t('setup.task.contacts.hint'),
      doneHint: t('setup.task.contacts.done'),
      cta: t('nav.contacts'),
      to: '/contacts',
      done: contactsQ.data === true
    },
    {
      key: 'template',
      label: t('setup.task.template.label'),
      hint: t('setup.task.template.hint'),
      doneHint: t('setup.task.template.done'),
      cta: t('nav.templates'),
      to: '/templates',
      done: templatesQ.data === true
    },
    {
      key: 'conversation',
      label: t('setup.task.conversation.label'),
      hint: t('setup.task.conversation.hint'),
      doneHint: t('setup.task.conversation.done'),
      cta: t('nav.inbox'),
      to: '/inbox',
      done: convs.data === true
    }
  ];

  const done = tasks.filter((task) => task.done).length;

  // Hide once dismissed, or once genuinely complete: but never hide on the
  // strength of failed queries, which would look identical to "all done".
  if (dismissed) return null;
  if (!pending && !failed && done === tasks.length) return null;

  if (pending) {
    return (
      <Card withBorder>
        <Stack gap="sm">
          <Skeleton height={18} width={160} />
          <Skeleton height={6} radius="xl" />
          <Group gap="md" wrap="wrap">
            {tasks.map((task) => <Skeleton key={task.key} height={54} style={{ flex: '1 1 200px' }} />)}
          </Group>
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder data-guide="dash-setup">
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Group gap="sm" align="center">
            <Text role="body" fw={600}>{t('setup.title')}</Text>
            <Badge size="sm" variant="light" className="rr-setup__count">
              {t('setup.progress.badge', { done, total: tasks.length })}
            </Badge>
          </Group>
          <Button size="xs" variant="subtle" onClick={dismiss}>
            {t('setup.dismiss')}
          </Button>
        </Group>

        <Progress
          value={(done / tasks.length) * 100}
          size="sm"
          radius="xl"
          aria-label={t('setup.progress', { done, total: tasks.length })}
        />

        {failed && <Alert color="warning">{t('setup.error')}</Alert>}

        <div className="rr-setup__steps">
          {tasks.map((task, i) => (
            <div key={task.key} className={`rr-setup__step${task.done ? ' is-done' : ''}`}>
              <span className="rr-setup__marker" aria-hidden>
                {task.done ? '✓' : i + 1}
              </span>
              {/* The tick and the fill are decoration; this is the part a
                  screen reader hears (§4 item 20). */}
              <span className="rr-visually-hidden">
                {task.done ? t('setup.step.done') : t('setup.step.todo')}
              </span>
              <Stack gap={4} style={{ minWidth: 0 }}>
                <Text role="bodySmall" fw={600}>{`${i + 1}. ${task.label}`}</Text>
                <Text role="caption" c="var(--rr-color-text-muted)">
                  {task.done ? task.doneHint : task.hint}
                </Text>
                {!task.done && (
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => navigate(task.to)}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    {task.cta}
                  </Button>
                )}
              </Stack>
            </div>
          ))}
        </div>
      </Stack>
    </Card>
  );
}
